import Analytics from '../analytics';
import SocialShareData from '../data/socialShareData';
import HWPTVAPI from '../hwpTvApi';
import { capitalize } from '../util/utilities';

const CN = '[SocialShareManager]';

const NETWORKS = Object.freeze({
	FACEBOOK: 'facebook',
	TWITTER: 'twitter',
});

const SHARE_URLS = {
	facebook: 'https://www.facebook.com/sharer/sharer.php?display=popup',
	twitter: 'https://twitter.com/intent/tweet',
};

export const SOCIAL_SHARE_REGEX = /socialShare-?(\w*):\/\/([\w|\d|_|-]*)/i;

class SocialShareManager {
	constructor() {
		// console.log(CN);

		document.addEventListener('click', this.onClick.bind(this));
	}

	initialize(options) {
		// console.log(CN, "[initialize]", { options });

		if (this.data) {
			this.data.destroy();
			this.data = null;
		}

		this.data = new SocialShareData(options);
		return this.data.isReady;
	}

	onClick(event) {
		// console.debug(CN, "[onClick]");

		if (event.target.nodeName === 'A' && SOCIAL_SHARE_REGEX.test(event.target.href)) {
			event.preventDefault();
			this.triggerShare(event.target.href);
		}
	}

	triggerShare(shareUri) {
		// console.log(CN, "[triggerShare]", { shareUri });

		const [, network = '', shareId] = SOCIAL_SHARE_REGEX.exec(shareUri);
		const shareData = this.data.getShareData(shareId);

		if (shareData) {
			const capitalizedNetwork = capitalize(network);
			Analytics.sendEvent('SocialShare', { socialNetwork: capitalizedNetwork, shareId });

			switch (network) {
				case NETWORKS.TWITTER:
					this.triggerTwitterShare(shareData);
					break;
				case NETWORKS.FACEBOOK:
					this.triggerFacebookShare(shareData);
					break;
				default:
					Analytics.sendEvent('SocialShare-Error', {
						error: `Invalid network: "${network}"`,
					});
					console.error(CN, `[Invalid SocialShare Network][${network}]`);
			}
		} else {
			Analytics.sendEvent('SocialShare-Error', {
				error: `Missing data for social share: "${shareId}"`,
			});
		}
	}

	/**
	 * Generates a URL that points at our API with the share information embedded as query parameters
	 * @param {Object} shareData Contains data to be used for share
	 * @param {String} network indexes into data's url object to grab appropriate url
	 * @returns {String} Url to be sent to social media network
	 */
	generateAPIShareURL(shareData, network) {
		// console.log(CN, '[generateAPIShareURL]', { shareData });

		const { title = '', url = {}, description = '', image = '' } = shareData;

		// Construct URL of our api endpoint that will contain OG meta tags
		const sharePageUrl = new URL(`${HWPTVAPI.baseUrl}/share`);
		sharePageUrl.searchParams.set('url', url[network]);
		sharePageUrl.searchParams.set('title', title);
		sharePageUrl.searchParams.set('description', description);
		sharePageUrl.searchParams.set('image', image);
		console.debug(CN, '[Social Share URL]', sharePageUrl.toString());

		return sharePageUrl.toString();
	}

	triggerFacebookShare(shareData) {
		// console.log(CN, '[triggerFacebookShare]', { shareData });

		// Construct URL of our api endpoint that will contain OG meta tags
		const apiPageUrl = this.generateAPIShareURL(shareData, NETWORKS.FACEBOOK);

		// Construct URL to open in window to trigger facebook share
		const fbShareUrl = new URL(`${SHARE_URLS.facebook}`);
		fbShareUrl.searchParams.set('u', apiPageUrl);

		window.open(fbShareUrl.toString(), '_blank');
	}

	triggerTwitterShare(shareData) {
		// console.log(CN, '[triggerTwitterShare]', { shareData });

		// Construct URL of our api endpoint that will contain OG meta tags
		const apiPageUrl = this.generateAPIShareURL(shareData, NETWORKS.TWITTER);
		const { title = '' } = shareData;

		// Construct URL to open in window to trigger twitter share
		const tweetUrl = new URL(`${SHARE_URLS.twitter}`);
		tweetUrl.searchParams.set('url', apiPageUrl);
		tweetUrl.searchParams.set('text', title);

		window.open(tweetUrl.toString(), '_blank');
	}
}

export default new SocialShareManager();
